// frontend/src/components/Financeiro/FinanceiroCredito.js
// =============================================
// ANÁLISE DE CRÉDITO POR CLIENTE
// ✅ Score de crédito em tempo real
// ✅ Aging de dívidas (0-30 / 31-60 / 61-90 / +90)
// ✅ Extrato detalhado por cliente (modal)
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATAÇÃO DINÂMICA
// 🔥 NOVO: configuracao (moeda+formatacao) + empresa vêm diretamente
//    da resposta do endpoint /api/financeiro/credito/clientes
//    — sem chamadas duplicadas a /api/empresas/minha/atual
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Icon } from './SharedComponents';

const API = '/api' || 'http://localhost:3000';

const NIVEL_COR = {
    'Excelente': '#10b981',
    'Bom': '#3b82f6',
    'Médio': '#f59e0b',
    'Risco': '#f97316',
    'Crítico': '#dc2626'
};

const NIVEL_BADGE = {
    'Excelente': 'success',
    'Bom': 'info',
    'Médio': 'warning',
    'Risco': 'danger',
    'Crítico': 'danger'
};

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

export default function FinanceiroCredito() {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filtroNivel, setFiltroNivel] = useState('');
    const [extrato, setExtrato] = useState(null);

    // 🔥 Config da empresa — começa com padrão
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // CARREGAR DADOS DE CRÉDITO
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/financeiro/credito/clientes`, { headers });
            if (res.data.success) {
                setDados(res.data);
                // 🔥 Aplicar config da resposta
                aplicarConfiguracao(res.data);
            }
        } catch (e) {
            console.error('Erro ao carregar dados:', e);
        } finally {
            setLoading(false);
        }
    }, [headers, aplicarConfiguracao]);

    useEffect(() => { carregar(); }, [carregar]);

    // =============================================
    // 🔥 ABRIR EXTRATO (aplica config da resposta)
    // =============================================
    const abrirExtrato = async (clienteId) => {
        try {
            const res = await axios.get(`${API}/financeiro/credito/cliente/${clienteId}`, { headers });
            if (res.data.success) {
                setExtrato(res.data);
                // 🔥 Também aplicar config (fallback)
                aplicarConfiguracao(res.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const clientes = (dados?.clientes || []).filter(c =>
        !filtroNivel || c.nivel === filtroNivel
    );
    const resumo = dados?.resumo || {};

    return (
        <div className="fin-credito">
            <header className="fin-section-header">
                <div className="fin-section-title">
                    <Icon name="group" size={24} color="var(--fin-primary)" />
                    <div>
                        <h2>Análise de Crédito</h2>
                        <p>Score em tempo real • {clientes.length} cliente(s)</p>
                    </div>
                </div>
                <div className="fin-section-actions">
                    <select value={filtroNivel} onChange={e => setFiltroNivel(e.target.value)} className="fin-filter-select">
                        <option value="">Todos os níveis</option>
                        <option value="Excelente">🟢 Excelente</option>
                        <option value="Bom">🔵 Bom</option>
                        <option value="Médio">🟡 Médio</option>
                        <option value="Risco">🟠 Risco</option>
                        <option value="Crítico">🔴 Crítico</option>
                    </select>
                    <button className="fin-btn-refresh" onClick={carregar}>
                        <Icon name="refresh" size={16} color="#fff" />
                    </button>
                </div>
            </header>

            {/* Resumo */}
            <section className="fin-credito-resumo">
                <div className="fin-credito-card" style={{ border: '2px solid #6b7280' }}>
                    <span>Total em Dívida</span>
                    <strong>{formatarMoeda(resumo.totalEmDivida)}</strong>
                </div>
                <div className="fin-credito-card verde">
                    <span>Excelentes</span>
                    <strong>{resumo.clientesExcelentes || 0}</strong>
                </div>
                <div className="fin-credito-card laranja">
                    <span>Risco</span>
                    <strong>{resumo.clientesRisco || 0}</strong>
                </div>
                <div className="fin-credito-card vermelho">
                    <span>Críticos</span>
                    <strong>{resumo.clientesCriticos || 0}</strong>
                </div>
            </section>

            {/* Tabela */}
            {loading ? (
                <div className="fin-loading"><div className="fin-spinner" />A carregar...</div>
            ) : clientes.length === 0 ? (
                <div className="fin-empty">
                    <Icon name="group" size={48} color="var(--fin-muted)" />
                    <h3>Sem clientes</h3>
                    <p>Não há clientes com faturas registadas.</p>
                </div>
            ) : (
                <div className="fin-table-wrapper">
                    <table className="fin-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Faturado</th>
                                <th>Pago</th>
                                <th>Em Dívida</th>
                                <th>Aging (0-30 / 31-60 / 61-90 / +90)</th>
                                <th>Score</th>
                                <th>Nível</th>
                                <th>Limite Sugerido</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientes.map(c => (
                                <tr key={c._id}>
                                    <td>
                                        <div className="fin-cliente-info">
                                            <strong>{c.nome}</strong>
                                            {c.nuit && <small>NUIT: {c.nuit}</small>}
                                        </div>
                                    </td>
                                    <td>{formatarMoeda(c.totalFaturado)}</td>
                                    <td>{formatarMoeda(c.totalPago)}</td>
                                    <td>
                                        <strong style={{ color: c.emDivida > 0 ? '#dc2626' : '#10b981' }}>
                                            {formatarMoeda(c.emDivida)}
                                        </strong>
                                    </td>
                                    <td>
                                        <div className="fin-aging-mini">
                                            <span className="aging-ok">{formatarMoeda(c.aging.d0_30)}</span>
                                            <span className="aging-warn">{formatarMoeda(c.aging.d31_60)}</span>
                                            <span className="aging-danger">{formatarMoeda(c.aging.d61_90)}</span>
                                            <span className="aging-critical">{formatarMoeda(c.aging.d90_plus)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fin-score-cell">
                                            <div className="fin-score-bar">
                                                <div
                                                    className="fin-score-fill"
                                                    style={{
                                                        width: `${c.score}%`,
                                                        background: NIVEL_COR[c.nivel]
                                                    }}
                                                />
                                            </div>
                                            <span style={{ color: NIVEL_COR[c.nivel] }}>{c.score}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`fin-badge badge-${NIVEL_BADGE[c.nivel]}`}>
                                            {c.nivel}
                                        </span>
                                    </td>
                                    <td>{formatarMoeda(c.limiteSugerido)}</td>
                                    <td>
                                        <button
                                            className="fin-btn-icon"
                                            onClick={() => abrirExtrato(c._id)}
                                            title="Ver extrato"
                                        >
                                            <Icon name="visibility" size={16} color="var(--fin-primary)" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de Extrato */}
            {extrato && (
                <div className="fin-modal-overlay" onClick={() => setExtrato(null)}>
                    <div className="fin-modal" onClick={e => e.stopPropagation()}>
                        <header className="fin-modal-header">
                            <h3>
                                <Icon name="person" size={18} color="var(--fin-primary)" />
                                Extrato — {extrato.cliente?.nome}
                            </h3>
                            <button className="fin-btn-close" onClick={() => setExtrato(null)}>
                                <Icon name="close" size={20} color="currentColor" />
                            </button>
                        </header>
                        <div className="fin-modal-body">
                            <div className="fin-extrato-resumo">
                                <div>
                                    <span>Total Faturado</span>
                                    <strong>{formatarMoeda(extrato.resumo?.totalFaturado)}</strong>
                                </div>
                                <div>
                                    <span>Total Pago</span>
                                    <strong>{formatarMoeda(extrato.resumo?.totalPago)}</strong>
                                </div>
                                <div>
                                    <span>Em Dívida</span>
                                    <strong>{formatarMoeda(extrato.resumo?.emDivida)}</strong>
                                </div>
                                <div>
                                    <span>Score</span>
                                    <strong>{extrato.resumo?.score} ({extrato.resumo?.nivel})</strong>
                                </div>
                            </div>

                            <table className="fin-table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Tipo</th>
                                        <th>Ref</th>
                                        <th>Descrição</th>
                                        <th>Débito</th>
                                        <th>Crédito</th>
                                        <th>Saldo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {extrato.movimentos?.map((m, i) => (
                                        <tr key={i}>
                                            <td>{formatarData(m.data)}</td>
                                            <td>{m.tipo}</td>
                                            <td><strong>{m.ref}</strong></td>
                                            <td>{m.descricao}</td>
                                            <td>{m.debito > 0 ? formatarMoeda(m.debito) : '—'}</td>
                                            <td>{m.credito > 0 ? formatarMoeda(m.credito) : '—'}</td>
                                            <td><strong>{formatarMoeda(m.saldo)}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}